import type { PortableTextComponentProps, PortableTextComponents } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import type { ElementType } from 'react';
import { alignMark, linkMark, classNameMark } from '@/lib/sanity/portable-text/marks';
import { TableRenderer } from '@/lib/sanity/portable-text/components/TableRenderer';
import {
    getLiftedClasses,
    mergeBlockClasses,
    unwrapLiftedClassNames,
} from '@/lib/sanity/portable-text/utils/liftClassNames';

// Block style renderer that lifts a block-covering `className` annotation onto the
// tag: a size class set on the whole block belongs on the element (paired
// font-size + line-height), not on an inline <span> (see utils/liftClassNames).
function styledBlock(Tag: ElementType, baseClass: string) {
    return function StyledBlock({ value, children }: PortableTextComponentProps<PortableTextBlock>) {
        const { classes, keys } = getLiftedClasses(value);
        const className = mergeBlockClasses(baseClass, classes);
        return <Tag className={className || undefined}>{unwrapLiftedClassNames(children, keys)}</Tag>;
    };
}

export const portableTextComponents: PortableTextComponents = {
    block: {
        h1: styledBlock('h1', 'heading-1'),
        h2: styledBlock('h2', 'heading-2'),
        h3: styledBlock('h3', 'heading-3'),
        h4: styledBlock('h4', 'heading-4'),
        h5: styledBlock('h5', 'heading-5'),
        h6: styledBlock('h6', 'heading-6'),
        'heading-additional': styledBlock('div', 'heading-additional'),
        blockquote: styledBlock('blockquote', ''),
        'hr-center': () => (
            <>
                <hr className="w-16 border-t-2 mx-auto border-white my-4 lg:mb-8" />
            </>
        ),
        'hr-left': () => (
            <>
                <hr className="w-16 border-t-2 border-white my-4 lg:mb-8" />
            </>
        ),
        'hr-right': () => (
            <>
                <hr className="w-16 border-t-2 ms-auto border-white my-4 lg:mb-8" />
            </>
        ),
        normal: styledBlock('p', ''),
    },
    marks: {
        link: linkMark,
        align: alignMark,
        className: classNameMark,
    },
    types: {
        table: TableRenderer,
    },
};
